import React, { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEPMContext } from '../../../../contexts/EPMContext';
import { useUserContext } from '../../../../contexts/UserContext';
import { permission } from '../../../../config/permission';
import useAutoSave, { notifyAutoSaved } from '../useAutoSave';
import { EPM_LIMITS } from '../../validation/epmRules';
import { decisionReasonSchema, decisionReasonFormatSchema, createValidator } from '../../validation/epmSchemas';
import EpmCharCounter from '../../EpmCharCounter';
import useEpmValidation from '../../validation/useEpmValidation';
import { Input } from '../../../UI';

const DecisionReason = ({ testData, setTestData, defaultReason }) => {
    const { id } = useParams();
    const { updateTest } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const { fieldErrors, validateFieldValue } = useEpmValidation();

    const [reason, setReason] = useState(testData?.triaged?.reason || defaultReason || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isEdited, setIsEdited] = useState(Boolean(testData?.triaged?.reason));
    const { saveLater, flush } = useAutoSave(testData);

    // Sync when a saved reason arrives; an emptied field stays empty while editing
    useEffect(() => {
        if (testData?.triaged?.reason) setReason(testData.triaged.reason);
    }, [testData?.triaged?.reason]);

    // Until someone types a reason, the prefill follows the score and rank
    useEffect(() => {
        if (!isEdited) setReason(defaultReason || '');
    }, [defaultReason, isEdited]);

    const currentStatus = testData?.triaged?.status;
    const validateFormat = useMemo(() => createValidator(decisionReasonFormatSchema), []);

    const updateTriaged = (status, updatedReason) => {
        if (!canUpdate) return;
        const payload = { status, reason: updatedReason || defaultReason };
        setTestData((prev) => ({ ...prev, triaged: payload }));
        const testId = testData?._id || testData?.id || id;
        if (testId && updateTest) {
            setIsSaving(true);
            updateTest(testId, { triaged: payload })
                .then(() => notifyAutoSaved())
                .catch((err) => console.error('Failed to update triage:', err))
                .finally(() => setIsSaving(false));
        }
    };

    const handleReasonChange = (e) => {
        const val = e.target.value;
        setIsEdited(true);
        setReason(val);
        validateFieldValue('reason', val, validateFormat);
        // Mirror into testData so save draft picks it up; keep existing status
        setTestData((prev) => ({
            ...prev,
            triaged: { ...prev?.triaged, reason: val },
        }));
        saveLater(
            'triaged.reason',
            (test) => ({ triaged: { reason: test?.triaged?.reason ?? '' } }),
            (test) => validateFormat(test?.triaged?.reason ?? '')
        );
    };

    const handleReasonBlur = () => flush('triaged.reason');

    const handleDecision = (status) => {
        if (!canUpdate) return;
        const reasonToUse = (reason || defaultReason || '').trim();
        try {
            decisionReasonSchema.validateSync(reasonToUse);
        } catch (err) {
            toast.error(err.message || `Decision reason must be at least ${EPM_LIMITS.DECISION_REASON_MIN} characters.`);
            return;
        }
        updateTriaged(status, reasonToUse);
        toast.success(`Idea marked as ${status === 'PARK' ? 'Parked' : 'Rejected'}.`);
    };

    return (
        <section className="epm-priority__decision relative">
            <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="priority-reason" className="stride-form-label !mb-0">Decision reason</label>
                <EpmCharCounter current={reason} max={EPM_LIMITS.DECISION_REASON_MAX} />
            </div>
            <Input
                id="priority-reason"
                type="text"
                value={reason}
                onChange={handleReasonChange}
                onBlur={handleReasonBlur}
                placeholder={defaultReason}
                error={fieldErrors.reason}
                className="w-full"
            />
            <div className="epm-priority__decision-actions">
                <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleDecision('PARK')}
                    className={`cursor-pointer transition-all ${
                        currentStatus === 'PARK'
                            ? '!bg-stride-body !text-white ring-1 ring-stride-body'
                            : 'hover:bg-surface-alt'
                    }`}
                >
                    Park
                </button>
                <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleDecision('REJECT')}
                    className={`cursor-pointer transition-all ${
                        currentStatus === 'REJECT'
                            ? '!bg-danger !text-white ring-1 ring-danger'
                            : 'hover:bg-rose-50'
                    }`}
                >
                    Reject
                </button>
            </div>
            <p>Prefilled from the score and viability — keep it or overwrite it. Saved with your name and time. <Info size={14} /></p>
        </section>
    );
};

export default DecisionReason;

